/**
 * Vue 3 composable pro backend multi-topic WS hub.
 *
 * Endpoint: `wss://<api>/api/ws/hub?token=<jwt>` (token z `useUserSession`).
 *
 * Wire protocol (per backend `app/api/ws.py`):
 * - klient → server: `{action: "subscribe"|"unsubscribe"|"ping", topic?}`
 * - server → klient: `{event, topic?, ok?, error?, data?}`
 *
 * Existující `useLaravelEcho` (Pusher-compat per-order chat WS) ponechán
 * paralelně — refactor chatu na nový hub až v V2. Tento hub přidává jen
 * novou infrastrukturu pro real-time notifications a status updates.
 *
 * Použití:
 *   const { subscribe } = useWebSocket();
 *   onMounted(() => {
 *     sub = subscribe(`order:${orderId}`, (event) => {
 *       if (event.event === 'order.status_changed') refresh();
 *     });
 *   });
 *   onUnmounted(() => sub?.unsubscribe());
 */
import useUserSession from '@/composables/useUserSession';

export type WSEventPayload = {
    event: string;
    topic?: string;
    data?: unknown;
    ok?: boolean;
    error?: string;
};

type Listener = (event: WSEventPayload) => void;

export type WSSubscription = { unsubscribe: () => void };

const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10_000, 30_000];

class WSClient {
    private socket: WebSocket | null = null;
    private listeners = new Map<string, Set<Listener>>();
    private connectAttempt = 0;
    private connecting = false;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private intentionalClose = false;

    subscribe(topic: string, listener: Listener): WSSubscription {
        let set = this.listeners.get(topic);
        if (!set) {
            set = new Set();
            this.listeners.set(topic, set);
        }
        set.add(listener);

        // Lazy connect — první subscriber otevře socket.
        void this.ensureConnected().then(() => {
            this.send({ action: 'subscribe', topic });
        });

        return {
            unsubscribe: () => {
                const listeners = this.listeners.get(topic);
                if (!listeners) return;
                listeners.delete(listener);
                if (listeners.size === 0) {
                    this.listeners.delete(topic);
                    this.send({ action: 'unsubscribe', topic });
                }
                if (this.listeners.size === 0) {
                    this.disconnect();
                }
            },
        };
    }

    getState(): 'closed' | 'connecting' | 'open' {
        if (!this.socket) return 'closed';
        if (this.socket.readyState === WebSocket.OPEN) return 'open';
        if (this.socket.readyState === WebSocket.CONNECTING) return 'connecting';
        return 'closed';
    }

    private async ensureConnected(): Promise<void> {
        if (this.socket?.readyState === WebSocket.OPEN) return;
        if (this.connecting) {
            return new Promise((resolve) => {
                const id = setInterval(() => {
                    if (!this.connecting) {
                        clearInterval(id);
                        resolve();
                    }
                }, 50);
            });
        }
        await this.connect();
    }

    private async connect(): Promise<void> {
        this.connecting = true;
        this.intentionalClose = false;
        try {
            const { getAccessToken } = useUserSession();
            const token = getAccessToken();
            const apiBaseUrl = import.meta.env.VITE_APP_API_URL as string | undefined;
            if (!token || !apiBaseUrl) {
                console.warn('[ws] cannot connect — missing token or VITE_APP_API_URL');
                this.connecting = false;
                return;
            }
            const wsBase = apiBaseUrl.replace(/^http/, 'ws').replace(/\/$/, '');
            const url = `${wsBase}/api/ws/hub?token=${encodeURIComponent(token)}`;

            this.socket = new WebSocket(url);
            this.socket.onopen = () => {
                this.connectAttempt = 0;
                this.connecting = false;
                for (const topic of this.listeners.keys()) {
                    this.send({ action: 'subscribe', topic });
                }
            };
            this.socket.onmessage = (e) => this.handleMessage(e.data);
            this.socket.onerror = (err) => {
                console.warn('[ws] error', err);
            };
            this.socket.onclose = () => {
                this.connecting = false;
                this.socket = null;
                if (!this.intentionalClose && this.listeners.size > 0) {
                    this.scheduleReconnect();
                }
            };
        } catch (err) {
            console.warn('[ws] connect failed', err);
            this.connecting = false;
            if (this.listeners.size > 0) {
                this.scheduleReconnect();
            }
        }
    }

    private scheduleReconnect(): void {
        if (this.reconnectTimer) return;
        const delay =
            RECONNECT_DELAYS_MS[Math.min(this.connectAttempt, RECONNECT_DELAYS_MS.length - 1)];
        this.connectAttempt += 1;
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            void this.connect();
        }, delay);
    }

    private handleMessage(raw: string): void {
        let payload: WSEventPayload;
        try {
            payload = JSON.parse(raw) as WSEventPayload;
        } catch (err) {
            console.warn('[ws] invalid JSON', raw, err);
            return;
        }
        const topic = payload.topic;
        if (!topic) return;
        const listeners = this.listeners.get(topic);
        if (!listeners || listeners.size === 0) return;
        for (const listener of listeners) {
            try {
                listener(payload);
            } catch (err) {
                console.warn('[ws] listener error', err);
            }
        }
    }

    private send(payload: Record<string, unknown>): void {
        if (this.socket?.readyState !== WebSocket.OPEN) return;
        try {
            this.socket.send(JSON.stringify(payload));
        } catch (err) {
            console.warn('[ws] send failed', err);
        }
    }

    private disconnect(): void {
        this.intentionalClose = true;
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
        if (this.socket) {
            try {
                this.socket.close(1000, 'intentional');
            } catch {
                // ignore
            }
            this.socket = null;
        }
        this.connectAttempt = 0;
    }
}

const wsClient = new WSClient();

/**
 * Vue composable wrapper. Vrací `subscribe(topic, listener)` který sám
 * **NEdělá** auto-cleanup — volající musí zavolat `.unsubscribe()` v
 * `onUnmounted`. Důvod: composable může být použit ve více scopes
 * (komponenta, store action) a explicitní cleanup je čistší než magic.
 */
const useWebSocket = () => ({
    subscribe: wsClient.subscribe.bind(wsClient),
    getState: wsClient.getState.bind(wsClient),
});

export default useWebSocket;
